import * as yup from 'yup';
import { DateTime } from 'luxon';

import { estimatePasswordCompliance } from '@britishcouncil/react-forms';

import { INPUT_DATE_FORMAT } from '.';

export const MIN_DATE_OF_BIRTH = '1900-01-01';
export const ADULT_MIN_AGE = 18;
export const NAME_REGEXP = /^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/;
export const LAST_NAME_REGEXP = /^([a-zA-Z() .-]{1,}\s?\'?-?[a-zA-Z().]*)$/;
export const TELEPHONE_REGEXP = /^^[0-9\\:\\-\\+\\(\\)]+$/;
export const STRING_DEFAULT_MAX_LENGTH = 50;
export const DEFAULT_MAX_STRING_MSG = 'Provided value is too long';

export const MALAYSIA_COUNTRY_ID = 53;

export const isMinor = (date: string | Date | undefined, format: string = INPUT_DATE_FORMAT) => {
  const age = calculateAge(date, format);
  return !!age && age < ADULT_MIN_AGE;
};

export const calculateAge = (date: string | Date | undefined, format: string = INPUT_DATE_FORMAT): number => {
  if (!date) return 0;
  let luxonDate = typeof date === 'string' ? DateTime.fromFormat(date, format) : DateTime.fromJSDate(date);
  return luxonDate.isValid ? -luxonDate.diffNow('years').years : 0;
};

export const transformDate = (value: string, rawValue: string) => {
  let dateValue = DateTime.fromISO(rawValue).isValid ? rawValue : undefined;
  return dateValue ? DateTime.fromISO(dateValue).toJSDate() : value;
};

export const VALIDATORS = {
  firstName: yup
    .string()
    .required('First Name is required.')
    .max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG)
    .matches(NAME_REGEXP, 'First name should not contain number or special characters.'),
  lastName: yup
  .string()
  .when('hasFamilyName', {
    is: true,
    then: (schema) =>
      schema
        .required('Last name is missing. If you don\'t have a last name please check the box.')
        .max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG)
        .matches(LAST_NAME_REGEXP, 'Last name should not contain number.'),
    otherwise: (schema) => schema.strip()
  }),
  dateOfBirth: yup
    .date()
    .required('Date of Birth is required.')
    .transform(transformDate)
    .typeError('Date of Birth is required.')
    .min(MIN_DATE_OF_BIRTH, 'Are you sure you were born that year?')
    .max(DateTime.local().toJSDate(), 'Are you sure you were born that year?'),
  email: yup.string().required('Email is required.').email('Please specify a valid email address.'),
  password: yup
    .string()
    .required('Password is required.')
    .test('password', 'Password is to weak.', (pass) => estimatePasswordCompliance(pass).isCompliant),
  telephone: yup
    .string()
    .matches(TELEPHONE_REGEXP, 'Only digits and :,+,-,(,) are allowed.')
    .max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),

  address: yup.object().shape({
    streetAddress1: yup.string().required('Address is required').max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
    streetAddress2: yup.string().max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
    streetAddress3: yup.string().max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
    streetAddress4: yup.string().max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
    city: yup.string().required('City is required').max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
    state: yup.string().max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
    postCode: yup.string().max(STRING_DEFAULT_MAX_LENGTH, DEFAULT_MAX_STRING_MSG),
    countryId: yup.number().required('Country/Territory is required'),
  }),
};
